import React, { useState, useRef, useEffect } from 'react';
import 'bulma/css/bulma.min.css';

export const Window = ({ 
  title = 'Window',
  width = 400,
  height = 300,
  initialPosition = { x: 50, y: 50 },
  onClose,
  closable = true,
  children
}) => {
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(true);
  const windowRef = useRef(null);

  // Handle mouse down on the header to start dragging
  const handleMouseDown = (e) => {
    setIsDragging(true);
    const rect = windowRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  // Handle mouse move while dragging
  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
  };

  // Handle mouse up to stop dragging
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add and remove event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  if (!isVisible) {
    return null;
  }

  return (
    <div 
      ref={windowRef}
      className="box"
      style={{
        position: 'absolute',
        zIndex: 1000,
        width: `${width}px`,
        height: `${height}px`,
        left: `${position.x}px`,
        top: `${position.y}px`,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 8px 16px rgba(10, 10, 10, 0.2)',
        cursor: isDragging ? 'grabbing' : 'default',
        userSelect: 'none'
      }}
    >
      {/* Window Header - Made smaller */}
      <div 
        className="window-header is-small"
        onMouseDown={handleMouseDown}
        style={{
          backgroundColor: '#f5f5f5',
          borderBottom: '1px solid #dbdbdb',
          padding: '0.25rem 0.75rem', // Reduced padding for smaller header
          cursor: isDragging ? 'grabbing' : 'grab',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '28px' // Explicitly set a smaller height
        }}
      >
        <div className="window-title is-size-7">{title}</div> {/* Using Bulma's is-size-7 for smaller text */}
        {closable && (
          <button 
            className="delete is-small" // Changed from is-medium to is-small
            onClick={() => {
              setIsVisible(false);
              if (onClose) {
                onClose();
              }
            }}
            aria-label="close"
            style={{
              cursor: 'pointer',
              backgroundColor: '#ff3860',
              borderRadius: '50%',
              height: '16px', // Smaller size
              width: '16px', // Smaller size
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: 'white',
              fontSize: '0.75rem', // Smaller font
              border: 'none',
              outline: 'none'
            }}
          />
        )}
      </div>
      
      {/* Window Content */}
      <div 
        className="window-content"
        style={{
          padding: '1rem',
          overflow: 'auto',
          flexGrow: 1
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default Window;